import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import { BiSupport } from "react-icons/bi";
import { MdIncompleteCircle } from "react-icons/md";

const API_URL = process.env.REACT_APP_API_URL;

const iconMap = {
  BiSupport: BiSupport,
};

const CallingLeadReport = () => {
  const [dashboardData, setDashboardData] = useState([]);
  const [completeCount, setCompleteCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageModalContent, setMessageModalContent] = useState({
    title: "",
    text: "",
    type: "",
    confirmAction: null,
  });

  const showCustomMessageModal = (title, text, type, confirmAction = null) => {
    setMessageModalContent({ title, text, type, confirmAction });
    setShowMessageModal(true);
  };

  const closeCustomMessageModal = () => {
    setShowMessageModal(false);
    setMessageModalContent({
      title: "",
      text: "",
      type: "",
      confirmAction: null,
    });
  };

  const getAuthToken = () => localStorage.getItem("token");

  const getAllStatuses = () => {
    return [
      { title: "Not Interested", icon: "BiSupport" },
      { title: "Already Purchased", icon: "BiSupport" },
      { title: "Looking in Low Budget", icon: "BiSupport" },
      { title: "Looking in Different Location", icon: "BiSupport" },
      { title: "In Follow Up Hot", icon: "BiSupport" },
      { title: "Looking in Commercial Use", icon: "BiSupport" },
      { title: "Details sent visit not done", icon: "BiSupport" },
      { title: "Call not Pickup", icon: "BiSupport" },
      { title: "In Follow Up", icon: "BiSupport" },
      { title: "In Follow Up Booking Done", icon: "BiSupport" },
      { title: "Invalid Mobile Number", icon: "BiSupport" },
      { title: "M Profile", icon: "BiSupport" },
      {
        title: "In Follow Up Site Visit Done Booking Pending",
        icon: "BiSupport",
      },
      {
        title: "In Follow Up Booking Done Payment Pending",
        icon: "BiSupport",
      },
      { title: "New", icon: "BiSupport" },
    ];
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = getAuthToken();
        if (!token) {
          showCustomMessageModal(
            "Authentication Error",
            "Authentication token not found. Please log in.",
            "error"
          );
          return;
        }

        const response = await fetch(`${API_URL}/calling-lead-report`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            showCustomMessageModal(
              "Authorization Error",
              "Unauthorized: Please log in again.",
              "error"
            );
          } else {
            const errorData = await response.json();
            showCustomMessageModal(
              "Error",
              errorData.message || "Failed to fetch dashboard data.",
              "error"
            );
          }
          throw new Error(response.statusText);
        }

        const rawData = await response.json();

        const getCount = (status) => {
          return rawData.data?.find((item) => item.status === status)?.count || 0;
        };

        const allStatuses = getAllStatuses();
        const transformedData = allStatuses.map((status) => ({
          title: status.title,
          value: getCount(status.title),
          icon: status.icon,
        }));

        setDashboardData(transformedData);
        setCompleteCount(rawData.complete_count || 0);
      } catch (err) {
        console.error("Fetch dashboard data error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // ═══════════ REUSABLE INLINE STYLES ═══════════
  const gradientBg =
    "linear-gradient(135deg, #1e1b4b 0%, #3b0f6b 50%, #4c1d95 100%)";

  const statusCardStyle = {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    padding: "16px",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "14px",
    boxShadow: "0 4px 16px rgba(15, 23, 42, 0.06)",
    cursor: "pointer",
    transition: "all 0.25s ease",
  };

  const statusIconStyle = {
    width: "50px",
    height: "50px",
    minWidth: "50px",
    borderRadius: "12px",
    background: gradientBg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 12px rgba(76, 29, 149, 0.35)",
    color: "#ffffff",
  };

  const statusTextStyle = {
    fontSize: "13px",
    fontWeight: 600,
    color: "#334155",
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    marginBottom: "4px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const statusCountStyle = {
    fontSize: "24px",
    fontWeight: 700,
    color: "#1e1b4b",
    lineHeight: 1,
  };

  const handleCardHoverIn = (e) => {
    e.currentTarget.style.transform = "translateY(-3px)";
    e.currentTarget.style.boxShadow = "0 10px 24px rgba(30, 27, 75, 0.15)";
    e.currentTarget.style.borderColor = "#c7d2fe";
  };

  const handleCardHoverOut = (e) => {
    e.currentTarget.style.transform = "translateY(0)";
    e.currentTarget.style.boxShadow = "0 4px 16px rgba(15, 23, 42, 0.06)";
    e.currentTarget.style.borderColor = "#e2e8f0";
  };

  // ═══════════ STATUS CARD COMPONENT (reusable) ═══════════
  const StatusCard = ({ title, value, icon }) => {
    const IconComponent = iconMap[icon];
    return (
      <div className="col-xs-12 col-sm-6 col-md-4 col-lg-4">
        <div
          style={statusCardStyle}
          onClick={() =>
            navigate(
              `/upload-property-lead-csv?status=${encodeURIComponent(title)}`
            )
          }
          onMouseEnter={handleCardHoverIn}
          onMouseLeave={handleCardHoverOut}
        >
          {IconComponent && (
            <div style={statusIconStyle}>
              <IconComponent size={26} />
            </div>
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={statusTextStyle} title={title}>
              {title}
            </div>
            <div style={statusCountStyle}>{value}</div>
          </div>
        </div>
      </div>
    );
  };

  const AllStatusCards = () => {
    const allStatuses = getAllStatuses();
    return (
      <>
        {allStatuses.map((status, index) => (
          <StatusCard
            key={index}
            title={status.title}
            value={0}
            icon={status.icon}
          />
        ))}
      </>
    );
  };

  // ═══════════ CUSTOM MODAL ═══════════
  const CustomMessageModal = () => {
    if (!showMessageModal) return null;

    return (
      <div
        className="modal show d-block"
        tabIndex="-1"
        role="dialog"
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      >
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{messageModalContent.title}</h5>
              <button
                type="button"
                className="btn-close"
                onClick={closeCustomMessageModal}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <p>{messageModalContent.text}</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeCustomMessageModal}
              >
                Close
              </button>
              {messageModalContent.confirmAction && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    messageModalContent.confirmAction();
                    closeCustomMessageModal();
                  }}
                >
                  Confirm
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "80vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return null;
  }

  return (
    <div className="padding_15">
      <div className="dashboard">
        {/* ═══════════ TOTAL CALLING LEADS HEADER ═══════════ */}
        <Row>
          <Col xl={12}>
            <div
              style={{
                position: "relative",
                overflow: "hidden",
                borderRadius: "16px",
                background: gradientBg,
                color: "#ffffff",
                boxShadow:
                  "0 12px 32px -10px rgba(30, 27, 75, 0.35), 0 4px 12px -4px rgba(30, 27, 75, 0.2)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "18px",
                  padding: "24px 28px",
                }}
              >
                <div
                  style={{
                    width: "64px",
                    height: "64px",
                    minWidth: "64px",
                    borderRadius: "50%",
                    background: "rgba(255, 255, 255, 0.15)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backdropFilter: "blur(6px)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                  }}
                >
                  <MdIncompleteCircle
                    style={{ fontSize: "32px", color: "#ffffff" }}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    gap: "16px",
                    flexWrap: "wrap",
                  }}
                >
                  <h6
                    style={{
                      margin: 0,
                      fontSize: "20px",
                      fontWeight: 600,
                      color: "rgba(255, 255, 255, 0.95)",
                      letterSpacing: "0.02em",
                    }}
                  >
                    Total Assigned Calling Leads :
                  </h6>
                  <h2
                    style={{
                      margin: 0,
                      fontSize: "42px",
                      fontWeight: 700,
                      color: "#ffffff",
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {completeCount}
                  </h2>
                </div>
              </div>

              {/* Decorative circles */}
              <div
                style={{
                  position: "absolute",
                  bottom: "-40px",
                  right: "-40px",
                  width: "160px",
                  height: "160px",
                  borderRadius: "50%",
                  background: "rgba(255, 255, 255, 0.08)",
                  pointerEvents: "none",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  top: "-30px",
                  right: "80px",
                  width: "100px",
                  height: "100px",
                  borderRadius: "50%",
                  background: "rgba(255, 255, 255, 0.05)",
                  pointerEvents: "none",
                }}
              />
            </div>
          </Col>
        </Row>

        <hr />

        {/* ═══════════ STATUS CARDS GRID ═══════════ */}
        <Row className="g-4">
          {dashboardData.length > 0 ? (
            dashboardData.map((item, index) => (
              <StatusCard
                key={index}
                title={item.title}
                value={item.value}
                icon={item.icon}
              />
            ))
          ) : (
            <AllStatusCards />
          )}
        </Row>

        {/* Info when all counts are zero */}
        {dashboardData.length > 0 &&
          dashboardData.every((item) => item.value === 0) && (
            <Row className="mt-4">
              <Col xs={12}>
                <div className="alert alert-info text-center">
                  <strong>Information:</strong> All lead status counts are zero.
                  Upload leads to see statistics.
                </div>
              </Col>
            </Row>
          )}
      </div>

      <CustomMessageModal />
    </div>
  );
};

export default CallingLeadReport;